import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaHome, FaUsers, FaUserAlt, FaTimes, FaBlog, FaGift, FaVideo, FaChevronLeft, FaChevronRight, FaCreditCard, FaQuestionCircle, FaChevronDown, FaChevronUp, FaAddressBook, FaLaptopCode, FaTags } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import logo from "../Assets/BooksCompl.png";

const AdminSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useTheme();
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);

  const navItems = [
    {
      path: "/dashboard",
      icon: FaHome,
      label: "Dashboard",
      hasSubMenu: true,
      subMenu: [
        { path: '/contacts', icon: FaAddressBook, label: 'Contacts Hub' },
      ]
    },
    { path: "/client", icon: FaUserAlt, label: "Client" },
    { path: "/employee", icon: FaUsers, label: "Employee" },
    { path: "/blogs", icon: FaBlog, label: "Blogs" },
    { path: "/questions", icon: FaQuestionCircle, label: "Questions" },
    { path: "/admin/payments", icon: FaCreditCard, label: "Payment Reports" },
    { path: "/promocodes", icon: FaTags, label: "Promocodes" },
    { path: "/referrals", icon: FaGift, label: "Referrals" },
    { path: "/meetings", icon: FaVideo, label: "Meetings" },
  ];


  const isDashboardActive = location.pathname === "/dashboard" || 
                           location.pathname === "/contacts";

  // Auto-open dropdown if on a submenu item, close when collapsed
  useEffect(() => {
    if (sidebarCollapsed) {
      setDashboardDropdownOpen(false);
    } else if (isDashboardActive) {
      setDashboardDropdownOpen(true);
    }
  }, [sidebarCollapsed, isDashboardActive]);

  return (
    <aside 
      className={`
        fixed top-0 left-0 z-40 h-screen
        bg-gradient-to-b from-primary-700 via-primary-800 to-dark-900
        dark:from-dark-900 dark:via-dark-800 dark:to-dark-900
        shadow-xl
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
        w-64
      `}
    >
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/10 via-transparent to-primary-500/10 pointer-events-none" />
      
      {/* Close Button (Mobile Only) */}
      <button 
        className="absolute top-4 right-4 p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200 lg:hidden"
        onClick={() => setSidebarOpen(false)}
      >
        <FaTimes className="w-5 h-5" />
      </button>

      {/* Collapse Toggle (Desktop Only) */}
      <button 
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 items-center justify-center bg-primary-600 dark:bg-dark-700 text-white rounded-full shadow-lg hover:bg-primary-500 dark:hover:bg-dark-600 transition-all duration-200 z-50"
        onClick={toggleSidebar}
      >
        {sidebarCollapsed ? (
          <FaChevronRight className="w-3 h-3" />
        ) : (
          <FaChevronLeft className="w-3 h-3" />
        )}
      </button>

      {/* Logo Container */}
      <div className={`flex items-center justify-center h-20 px-4 border-b border-white/10 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
        <img 
          src={logo} 
          alt="Logo" 
          className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:w-10 lg:h-10' : 'h-12'} object-contain`}
        />
      </div>

      {/* Navigation Links */}
      <nav className="mt-6 px-3 relative z-10">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.hasSubMenu && isDashboardActive);
            const Icon = item.icon;
            
            if (item.hasSubMenu) {
              // If sidebar is collapsed, show as regular link
              if (sidebarCollapsed) {
                return (
                  <li key={item.path}>
                    <Link
                      to="/dashboard"
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-xl
                        transition-all duration-200 ease-out
                        group relative overflow-hidden
                        ${isDashboardActive 
                          ? 'bg-white/20 text-white shadow-lg' 
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                        }
                        lg:justify-center lg:px-2
                      `}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {isDashboardActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-secondary-500 rounded-r-full" />
                      )}
                      <span className={`
                        relative flex-shrink-0
                        ${isDashboardActive ? 'text-secondary-400' : 'text-white/70 group-hover:text-secondary-400'}
                        transition-all duration-200
                        ${isDashboardActive ? 'scale-110' : 'group-hover:scale-110'}
                      `}>
                        <Icon className="w-5 h-5" />
                        {isDashboardActive && (
                          <span className="absolute inset-0 blur-sm bg-secondary-400/50 rounded-full" />
                        )}
                      </span>
                      <span className="
                        hidden lg:block absolute left-full ml-3 px-3 py-2
                        bg-dark-800 text-white text-sm font-medium
                        rounded-lg shadow-xl opacity-0 pointer-events-none
                        group-hover:opacity-100 group-hover:pointer-events-auto
                        transition-all duration-200 whitespace-nowrap z-50
                      ">
                        {item.label}
                        <span className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-dark-800" />
                      </span>
                    </Link>
                  </li>
                );
              }
              
              // If sidebar is expanded, show dropdown
              return (
                <li key={item.path}>
                  <div className="space-y-1">
                    <Link
                      to="/dashboard"
                      onClick={() => setDashboardDropdownOpen(!dashboardDropdownOpen)}
                      className={`
                        w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl
                        transition-all duration-200 ease-out
                        group relative overflow-hidden
                        ${isDashboardActive 
                          ? 'bg-white/20 text-white shadow-lg' 
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {isDashboardActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-secondary-500 rounded-r-full" />
                        )}
                        <span className={`
                          relative flex-shrink-0
                          ${isDashboardActive ? 'text-secondary-400' : 'text-white/70 group-hover:text-secondary-400'}
                          transition-all duration-200
                          ${isDashboardActive ? 'scale-110' : 'group-hover:scale-110'}
                        `}>
                          <Icon className="w-5 h-5" />
                          {isDashboardActive && (
                            <span className="absolute inset-0 blur-sm bg-secondary-400/50 rounded-full" />
                          )}
                        </span>
                        <span className="font-medium whitespace-nowrap">
                          {item.label}
                        </span>
                      </div>
                      {dashboardDropdownOpen ? (
                        <FaChevronUp className="w-3 h-3 text-white/70" />
                      ) : (
                        <FaChevronDown className="w-3 h-3 text-white/70" />
                      )}
                    </Link>
                    
                    {dashboardDropdownOpen && (
                      <ul className="ml-4 mt-1 space-y-1 border-l-2 border-white/20 pl-2">
                        {item.subMenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          
                          return (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path}
                                className={`
                                  flex items-center gap-3 px-4 py-2 rounded-lg
                                  transition-all duration-200 ease-out
                                  group relative
                                  ${isSubActive 
                                    ? 'bg-white/20 text-white' 
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                  }
                                `}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <SubIcon className="w-4 h-4" />
                                <span className="text-sm font-medium">{subItem.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              );
            }
            
            return (
              <li key={item.path}>
                <Link 
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl
                    transition-all duration-200 ease-out
                    group relative overflow-hidden
                    ${isActive 
                      ? 'bg-white/20 text-white shadow-lg' 
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                    }
                    ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-secondary-500 rounded-r-full" />
                  )}
                  
                  {/* 3D Icon effect */}
                  <span className={`
                    relative flex-shrink-0
                    ${isActive ? 'text-secondary-400' : 'text-white/70 group-hover:text-secondary-400'}
                    transition-all duration-200
                    ${isActive ? 'scale-110' : 'group-hover:scale-110'}
                  `}>
                    <Icon className="w-5 h-5" />
                    {isActive && (
                      <span className="absolute inset-0 blur-sm bg-secondary-400/50 rounded-full" />
                    )}
                  </span>
                  
                  {/* Label - hidden when collapsed */}
                  <span className={`
                    font-medium whitespace-nowrap
                    transition-all duration-300
                    ${sidebarCollapsed ? 'lg:hidden lg:opacity-0 lg:w-0' : 'opacity-100'}
                  `}>
                    {item.label}
                  </span>

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <span className="
                      hidden lg:block absolute left-full ml-3 px-3 py-2
                      bg-dark-800 text-white text-sm font-medium
                      rounded-lg shadow-xl opacity-0 pointer-events-none
                      group-hover:opacity-100 group-hover:pointer-events-auto
                      transition-all duration-200 whitespace-nowrap z-50
                    ">
                      {item.label}
                      <span className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-dark-800" />
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
    </aside>
  );
};

export default AdminSidebar;

